import React, {useEffect, useState} from 'react';
import {WebSocketMessage, websocketService} from '../services/websocketService';
import { CookieManager } from '../utils/cookieManager';
import { ActiveGamesPanel } from './ActiveGamesPanel';
import { useWebSocket } from '../contexts/WebSocketContext';
import logger from '../utils/debugLogger';

export interface GameSession {
  gameId: string;
  primers: string[];
  worldContext: string;
  winConditions: string;
  currentScene: string;
  sceneDescription: string;
  imageUrl: string;
  isComplete: boolean;
}

interface GameInitializerProps {
  onGameStart: (gameSession: GameSession) => void;
  onResumeGame: (gameId: string, primers: string[]) => void;
}

const GameInitializer: React.FC<GameInitializerProps> = ({ onGameStart, onResumeGame }) => {
  const [primerInput, setPrimerInput] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingSteps, setLoadingSteps] = useState<string[]>([]);
  
  // Use the WebSocket context instead of direct service
  const { isConnected: wsConnected, connect, error: wsError } = useWebSocket();

  // Handle WebSocket connection errors
  useEffect(() => {
    if (wsError) {
      logger.error('WebSocket connection error in GameInitializer:', wsError);
      setError(`Connection error: ${wsError.message}`);
    }
  }, [wsError]);
  
  useEffect(() => {
    logger.info('GameInitializer: WebSocket connected state:', wsConnected);
  }, [wsConnected]);

  useEffect(() => {
    // Subscribe to messages with current primers in scope
    const unsubscribeMessage = websocketService.onMessage((message: WebSocketMessage) => {
      handleWebSocketMessage(message);
    });

    return () => {
      unsubscribeMessage();
    };
  }, [primerInput]); // Re-subscribe when primerInput changes

  const handleWebSocketMessage = (message: WebSocketMessage) => {
    switch (message.type) {
      case 'game_init_started':
        setLoadingSteps(['🌍 ' + (message.data.message || 'Generating your unique world...')]);
        break;

      case 'progress':
        const progressMessage = message.data.message;
        if (progressMessage.includes('Claude')) {
          setLoadingSteps(prev => [...prev.filter(s => !s.includes('Creating your quest')), '🎯 Creating your quest objectives...']);
        } else if (progressMessage.includes('scene')) {
          setLoadingSteps(prev => [...prev.filter(s => !s.includes('artwork')), '🎨 Generating scene artwork...']);
        }
        break;

      case 'game_ready':
        setLoadingSteps(prev => [...prev, '⚡ Your adventure is ready!']);
        
        // Parse primers from input
        const primers = primerInput.split(',').map(p => p.trim().toLowerCase());
        
        // Create game session from WebSocket data
        const gameSession: GameSession = {
          gameId: message.gameId || `game-${Date.now()}`,
          primers: primers,  // Use the parsed primers from the form
          worldContext: message.data.worldContext || '',
          winConditions: message.data.winConditions || '',
          currentScene: message.data.narrative || message.data.scene,
          sceneDescription: message.data.sceneDescription || message.data.sceneSummary,
          imageUrl: message.data.imageUrl,
          isComplete: false
        };
        
        logger.info('GameInitializer - Creating game session with primers:', primers);
        logger.debug('GameInitializer - Full game session:', logger.sanitize(gameSession));
        
        // Save game session to cookie
        CookieManager.saveGameSession(gameSession.gameId, primers);
        
        setTimeout(() => {
          onGameStart(gameSession);
        }, 500); // Small delay to show completion message
        break;

      case 'error':
        setError(message.data.message || 'Failed to initialize game');
        setLoading(false);
        setLoadingSteps([]);
        break;
    }
  };

  const validatePrimers = (): { valid: boolean; primers: string[] } => {
    const trimmedInput = primerInput.trim();
    if (!trimmedInput) {
      setError('Please enter three words separated by commas');
      return { valid: false, primers: [] };
    }

    const primers = trimmedInput.split(',').map(p => p.trim().toLowerCase());
    
    if (primers.length !== 3) {
      setError('Please enter exactly three words separated by commas');
      return { valid: false, primers: [] };
    }

    for (const primer of primers) {
      if (!primer || !/^[a-z]+$/.test(primer) || primer.length < 2 || primer.length > 15) {
        setError('Each word must be 2-15 alphabetic characters');
        return { valid: false, primers: [] };
      }
    }
    
    return { valid: true, primers };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validation = validatePrimers();
    if (!validation.valid || !wsConnected) return;

    setLoading(true);
    setError(null);
    setLoadingSteps([]);

    try {
      // Ensure connection before initializing game
      if (!wsConnected) {
        logger.info('GameInitializer: Connecting WebSocket before game initialization');
        await connect();
      }
      
      // Initialize game via WebSocket
      await websocketService.initGame(validation.primers);
    } catch (err) {
      logger.error('Game initialization error:', err);
      setError(err instanceof Error ? err.message : 'Failed to initialize game');
      setLoading(false);
      setLoadingSteps([]);
    }
  };

  return (
    <div className="game-initializer">
      <h2>Unchained</h2>
      <div className={`connection-status ${wsConnected ? 'connected' : 'connecting'}`}>
        <span className="status-indicator"></span>
        <span className="status-text">
          {wsConnected ? 'Connected to game server' : 'Connecting to game server...'}
        </span>
      </div>
      <p>
        Enter three words that will shape your world and destiny. These words will determine:
      </p>
      <ul>
        <li>✨ The nature of your world</li>
        <li>✨ Your ultimate goal</li>
        <li>✨ The challenges you'll face</li>
      </ul>
      
      <form onSubmit={handleSubmit}>
        <div className="primer-input">
          <label htmlFor="primers">Your three words:</label>
          <input
            type="text"
            id="primers"
            value={primerInput}
            onChange={(e) => {
              setPrimerInput(e.target.value);
              setError(null);
            }}
            placeholder="Enter three words separated by commas"
            disabled={!wsConnected}
          />
        </div>
        
        {error && <div className="error-message">{error}</div>}
        
        {loading && (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <div className="loading-steps">
              {loadingSteps.map((step, index) => (
                <div key={index} className="loading-step">
                  {step}
                </div>
              ))}
            </div>
          </div>
        )}
        
        <button type="submit" disabled={!primerInput.trim() || loading || !wsConnected}>
          {loading ? 'Creating Your World...' : !wsConnected ? 'Connecting...' : 'Begin Journey'}
        </button>
      </form>
      
      <div className="examples">
        <h3>Example combinations:</h3>
        <ul>
          <li onClick={() => setPrimerInput('ancient, technology, magic')}>
            ancient, technology, magic
          </li>
          <li onClick={() => setPrimerInput('revenge, justice, peace')}>
            revenge, justice, peace
          </li>
          <li onClick={() => setPrimerInput('dragon, crystal, prophecy')}>
            dragon, crystal, prophecy
          </li>
        </ul>
      </div>
      <ActiveGamesPanel onResumeGame={onResumeGame} />
    </div>
  );
};

export default GameInitializer;